var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
/**
 * The template of each check, spelled out: the translation-integrity gate checks a literal
 * id against the catalogues, a composed one only by pattern.
 */
var SENTENCE = {
    K01: 'ui-czna-authorities.corpusQuality.finding.sentence.K01',
    K02: 'ui-czna-authorities.corpusQuality.finding.sentence.K02',
    K03: 'ui-czna-authorities.corpusQuality.finding.sentence.K03',
    K04: 'ui-czna-authorities.corpusQuality.finding.sentence.K04',
    K05: 'ui-czna-authorities.corpusQuality.finding.sentence.K05',
    K06: 'ui-czna-authorities.corpusQuality.finding.sentence.K06',
    K07: 'ui-czna-authorities.corpusQuality.finding.sentence.K07',
    K08: 'ui-czna-authorities.corpusQuality.finding.sentence.K08',
    K09: 'ui-czna-authorities.corpusQuality.finding.sentence.K09',
    Q1: 'ui-czna-authorities.corpusQuality.finding.sentence.Q1',
    Q2: 'ui-czna-authorities.corpusQuality.finding.sentence.Q2',
    Q3: 'ui-czna-authorities.corpusQuality.finding.sentence.Q3',
    Q4: 'ui-czna-authorities.corpusQuality.finding.sentence.Q4',
    Q5: 'ui-czna-authorities.corpusQuality.finding.sentence.Q5',
    Q6: 'ui-czna-authorities.corpusQuality.finding.sentence.Q6',
    S1: 'ui-czna-authorities.corpusQuality.finding.sentence.S1',
    MONTHLY_DANGLING_SEE_ALSO: 'ui-czna-authorities.corpusQuality.finding.sentence.MONTHLY_DANGLING_SEE_ALSO',
    MONTHLY_DUP_HEADING: 'ui-czna-authorities.corpusQuality.finding.sentence.MONTHLY_DUP_HEADING',
    noDetail: 'ui-czna-authorities.corpusQuality.finding.sentence.noDetail',
};
/** The relation a `$w` states, as the templates select it; anything else is `other`. */
var relation = function (field) {
    var w = field === null || field === void 0 ? void 0 : field.relationCode;
    return w && ['a', 'b', 'g', 'h'].includes(w) ? w : 'other';
};
/** What the other side of a K07 pair should say back: earlier ↔ later, broader ↔ narrower. */
var COUNTER = {
    a: 'b', b: 'a', g: 'h', h: 'g',
};
/**
 * The sentence of one finding (CZNA-1408): which template, filled with what. The words are
 * in the translations, per check; this picks the template and hands it the STRUCTURED data
 * the API sends (`detail`) — never a parse of `matchedContent`, which stays the whitelist
 * key. A run stored before CZNA-1408 has no detail: a check whose sentence needs it then
 * says only that the MARC field shows the details.
 *
 * @param checkCode the check (`K06`, `Q6_372A`, …)
 * @param finding   the finding
 * @param names     how the row names the record and its counterpart
 */
export var findingSentence = function (checkCode, finding, names) {
    var _a, _b, _c, _d, _e;
    var detail = (_a = finding.detail) !== null && _a !== void 0 ? _a : null;
    var field = (_b = detail === null || detail === void 0 ? void 0 : detail.field) !== null && _b !== void 0 ? _b : null;
    var base = { record: names.record, target: names.target };
    var noDetail = { id: SENTENCE.noDetail, values: base };
    var code = checkCode.startsWith('Q6_') ? 'Q6' : checkCode;
    var id = SENTENCE[code];
    switch (code) {
        case 'K01':
        case 'K02':
        case 'K06':
            if (!field)
                return noDetail;
            return { id: id, values: __assign(__assign({}, base), { relation: relation(field) }) };
        case 'K07': {
            if (!field)
                return noDetail;
            var w = relation(field);
            return {
                id: id,
                values: __assign(__assign({}, base), { relation: w, counter: (_c = COUNTER[w]) !== null && _c !== void 0 ? _c : 'other' }),
            };
        }
        case 'K04':
            return {
                id: id,
                values: __assign(__assign({}, base), { designator: (field === null || field === void 0 ? void 0 : field.designator) || 'none' }),
            };
        case 'Q5':
            if (!(field === null || field === void 0 ? void 0 : field.designator))
                return noDetail;
            return { id: id, values: __assign(__assign({}, base), { designator: field.designator }) };
        case 'K08':
            if ((detail === null || detail === void 0 ? void 0 : detail.linkText) == null || (detail === null || detail === void 0 ? void 0 : detail.targetText) == null)
                return noDetail;
            return {
                id: id,
                values: __assign(__assign({}, base), { linkText: detail.linkText, targetText: detail.targetText }),
            };
        case 'MONTHLY_DANGLING_SEE_ALSO':
            if (!(detail === null || detail === void 0 ? void 0 : detail.linkText))
                return noDetail;
            return { id: id, values: __assign(__assign({}, base), { linkText: detail.linkText }) };
        case 'Q6': {
            var value = (_d = field === null || field === void 0 ? void 0 : field.subfields.find(function (s) { return s.value != null; })) === null || _d === void 0 ? void 0 : _d.value;
            if (!field || value == null)
                return noDetail;
            return { id: SENTENCE.Q6, values: __assign(__assign({}, base), { tag: field.tag, value: value }) };
        }
        case 'S1': {
            // CZNA-1427: a counterpart is the subject heading the value is linked to — then it is
            // another designation (368 $c); without one, the sentence names both places.
            var value = (_e = field === null || field === void 0 ? void 0 : field.subfields.find(function (s) { return s.value != null; })) === null || _e === void 0 ? void 0 : _e.value;
            if (value == null)
                return noDetail;
            return {
                id: id,
                values: __assign(__assign({}, base), { value: value, linked: finding.counterpartId ? 'yes' : 'no' }),
            };
        }
        case 'K03':
        case 'K05':
        case 'K09':
        case 'Q1':
        case 'Q2':
        case 'Q3':
        case 'Q4':
        case 'MONTHLY_DUP_HEADING':
            return { id: id, values: base };
        default:
            return noDetail;
    }
};
